// panel-edit-session.ts — PSTUDIO-1433

/**
 * One edit of one cell in the Expanded Cell View, from the moment the user
 * starts editing until the value is committed. The commit path reads ONLY this
 * — never the live selection — so a commit that happens after the selection has
 * already moved still targets the cell the edit belongs to.
 */
export interface PanelEditSession {
  rowId: string;
  columnId: string;
  /** The cell's value when the edit began — the "changed or not" baseline. */
  baseline: string;
  /**
   * PSTUDIO-4415: the text the JSON editor SHOWED when the edit began, when that
   * is not `baseline` itself — the editor lays a stored one-liner out for
   * display. A draft equal to it is an edit that netted to nothing, not a
   * change. Absent for the textarea, which shows the stored value as it is.
   */
  shownBaseline?: string;
  /** Committed value + JSON map sibling, for one atomic undo (PSTUDIO-2154). */
  before: Record<string, unknown>;
  /** Whether this cell is written as JSON — captured with the session so the
   *  commit canonicalizes the way THIS cell needs, not the way the currently
   *  selected one would. */
  isJsonColumn: boolean;
}

/**
 * Does an existing session already cover this cell?
 *
 * A session spans the whole edit, not one focus: focus legitimately leaves and
 * returns (clicking the panel's own label and then back into the editor is the
 * ordinary case, and the ticket forbids saving on it). Re-capturing the
 * baseline on that return would re-baseline against the DRAFT, so the eventual
 * commit would see "nothing changed" and silently drop the user's edit.
 */
export function isSessionForCell(
  session: PanelEditSession | null,
  rowId: string | undefined,
  columnId: string | undefined
): boolean {
  if (!session || !rowId || !columnId) return false;
  return session.rowId === rowId && session.columnId === columnId;
}

export type PanelCommitAction = 'save' | 'clear-dirty' | 'revert' | 'noop';

/**
 * What a commit request should do, given the session and the draft the store
 * currently holds FOR THAT SESSION'S CELL.
 *
 * - no session → `noop`. Either nothing was edited, or the session was already
 *   consumed by an earlier commit in this same keystroke (Enter commits, then
 *   moves the selection, which blurs the editor and requests a second commit).
 *   Saving there is the double-save the ticket rules out.
 * - draft differs from the baseline → `save`.
 * - draft equals the baseline → `clear-dirty`: nothing to persist, but the row
 *   must not stay marked dirty from keystrokes that netted out to no change.
 * - draft equals the text the editor SHOWED (`shownBaseline`, a display layout
 *   of the baseline) → `revert` (PSTUDIO-4415): the edit netted to nothing as
 *   far as the user can see, so nothing is persisted, and the in-memory row,
 *   which the draft writes already moved to the layout, goes back to the stored
 *   value. Saving here rewrote a one-liner nobody changed (PSTUDIO-1092: "Do not
 *   change the JSON automatically").
 */
export function resolvePanelCommitAction(
  session: PanelEditSession | null,
  draft: string
): PanelCommitAction {
  if (!session) return 'noop';
  if (draft === session.baseline) return 'clear-dirty';
  if (session.shownBaseline !== undefined && draft === session.shownBaseline) {
    return 'revert';
  }
  return 'save';
}

export interface PanelCommitPlan {
  rowId: string;
  columnId: string;
  /** The draft to persist, before column canonicalization. */
  value: string;
  /** The pre-edit snapshot the undo command restores. */
  before: Record<string, unknown>;
  isJsonColumn: boolean;
}

/**
 * WHAT a commit should write, resolved entirely from the session.
 *
 * This is the single place that answers "which cell, which value, which undo
 * baseline", so the answer cannot drift back to the live selection — the defect
 * this replaced. The caller only performs the store writes; it never picks the
 * target itself. Returns `null` when there is nothing to save — either because
 * there is no session at all, or because the draft still equals the baseline,
 * in which case the caller only clears the row's dirty flag.
 */
export function buildPanelCommitPlan(
  session: PanelEditSession | null,
  draft: string
): PanelCommitPlan | null {
  if (resolvePanelCommitAction(session, draft) !== 'save' || !session) {
    return null;
  }
  return {
    rowId: session.rowId,
    columnId: session.columnId,
    value: draft,
    before: session.before,
    isJsonColumn: session.isJsonColumn,
  };
}
